import React, { useEffect, useState } from 'react';
import { useParams, Link } from 'react-router-dom';
import { getImportReconciliation, getImportBatch } from '../lib/api';
import { formatDateTime } from '../lib/utils';
import { ArrowLeft, Users, UserCheck, Truck, MapPin, CheckCircle, AlertCircle, Loader2, FileSpreadsheet } from 'lucide-react';

export default function ImportReconciliationPage() {
  const { batchId } = useParams();
  const [data, setData] = useState(null);
  const [batch, setBatch] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  useEffect(() => {
    setLoading(true);
    Promise.all([
      getImportReconciliation(batchId),
      getImportBatch(batchId),
    ]).then(([reconRes, batchRes]) => {
      setData(reconRes.data);
      setBatch(batchRes.data);
      setLoading(false);
    }).catch(err => {
      setError(err.response?.data?.detail || 'Failed to load reconciliation data');
      setLoading(false);
    });
  }, [batchId]);

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64" data-testid="reconciliation-loading">
        <Loader2 size={32} className="animate-spin text-boss-violet" />
      </div>
    );
  }

  if (error) {
    return (
      <div className="space-y-4" data-testid="reconciliation-error">
        <Link to="/boss/fa/import/history" className="inline-flex items-center gap-2 text-sm text-boss-text-muted hover:text-boss-violet">
          <ArrowLeft size={16} /> Back to Import History
        </Link>
        <div className="bg-red-50 border border-red-200 rounded-xl p-6 text-center">
          <AlertCircle size={32} className="mx-auto text-red-500 mb-3" />
          <p className="text-sm text-red-700">{error}</p>
        </div>
      </div>
    );
  }

  const masterSections = [
    { key: 'parties', label: 'Parties', icon: Users, color: 'text-blue-600 bg-blue-50 border-blue-200' },
    { key: 'salesmen', label: 'Salesmen', icon: UserCheck, color: 'text-green-600 bg-green-50 border-green-200' },
    { key: 'transports', label: 'Transports', icon: Truck, color: 'text-orange-600 bg-orange-50 border-orange-200' },
    { key: 'stations', label: 'Stations', icon: MapPin, color: 'text-purple-600 bg-purple-50 border-purple-200' },
  ];

  return (
    <div className="max-w-4xl mx-auto space-y-6" data-testid="reconciliation-page">
      <Link to="/boss/fa/import/history" className="inline-flex items-center gap-2 text-sm text-boss-text-muted hover:text-boss-violet">
        <ArrowLeft size={16} /> Back to Import History
      </Link>

      {/* Batch Header */}
      <div className="bg-white rounded-xl p-6 shadow-sm border border-boss-border">
        <div className="flex items-start justify-between">
          <div>
            <h1 className="text-xl font-bold text-boss-text" data-testid="reconciliation-title">Import Reconciliation</h1>
            <p className="text-sm text-boss-text-muted mt-1">
              Batch: {batch?.file_name || batchId}
            </p>
            {batch?.upload_time && (
              <p className="text-xs text-boss-text-muted mt-0.5">Imported: {formatDateTime(batch.upload_time)}</p>
            )}
          </div>
          <div className="flex items-center gap-3">
            <div className="text-center bg-boss-violet/5 px-4 py-2 rounded-xl border border-boss-violet/10">
              <div className="text-2xl font-bold text-boss-violet">{data?.total_invoices || 0}</div>
              <div className="text-[10px] text-boss-text-muted">Invoices</div>
            </div>
            <div className="text-center bg-boss-surface-2 px-4 py-2 rounded-xl border border-boss-border">
              <div className="text-2xl font-bold text-boss-text">{data?.new_masters_count || 0}</div>
              <div className="text-[10px] text-boss-text-muted">New Masters</div>
            </div>
          </div>
        </div>
      </div>

      {/* Master Data Sections */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        {masterSections.map(section => {
          const items = data?.new_masters?.[section.key] || [];
          const Icon = section.icon;
          return (
            <div key={section.key} className="bg-white rounded-xl p-5 shadow-sm border border-boss-border" data-testid={`reconciliation-${section.key}`}>
              <div className="flex items-center justify-between mb-4">
                <div className="flex items-center gap-2">
                  <div className={`w-8 h-8 rounded-lg flex items-center justify-center ${section.color.split(' ').slice(1, 3).join(' ')}`}>
                    <Icon size={16} className={section.color.split(' ')[0]} />
                  </div>
                  <h3 className="text-sm font-semibold text-boss-text">New {section.label}</h3>
                </div>
                <span className="text-xs font-medium px-2 py-0.5 rounded-full bg-boss-surface-2 text-boss-text-muted">
                  {items.length} new
                </span>
              </div>
              {items.length > 0 ? (
                <div className="space-y-1.5 max-h-48 overflow-y-auto">
                  {items.map((name, i) => (
                    <div key={i} className="flex items-center gap-2 px-3 py-2 bg-boss-surface-2 rounded-lg text-sm">
                      <CheckCircle size={14} className="text-boss-success flex-shrink-0" />
                      <span className="truncate">{name}</span>
                    </div>
                  ))}
                </div>
              ) : (
                <div className="text-center py-6 text-boss-text-muted">
                  <FileSpreadsheet size={24} className="mx-auto mb-2 opacity-40" />
                  <p className="text-xs">No new {section.label.toLowerCase()} created in this batch</p>
                </div>
              )}
            </div>
          );
        })}
      </div>

      {/* Summary */}
      {data?.new_masters_count === 0 && (
        <div className="bg-green-50 border border-green-200 rounded-xl p-6 text-center">
          <CheckCircle size={32} className="mx-auto text-green-500 mb-3" />
          <h3 className="text-sm font-semibold text-green-800">All Master Data Matched</h3>
          <p className="text-xs text-green-600 mt-1">No new parties, salesmen, transports, or stations were created during this import. All records matched existing master data.</p>
        </div>
      )}
    </div>
  );
}
